import { Action } from "@/api-schema-enums";
import { Can, useAbility } from "@/casl-provider";
import { useVehicleDeleteMutation } from "@/common/api/vehicle/use-vehicle-delete.mutation";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { createRowNavigatePath } from "@/components/data-table/_helpers/create-row-navigate-path";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { VehicleTable } from "@/components/data-table/predefined/vehicle-table";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

type Vehicle = RowWithIdentifier & {
  color?: string | null;
  registrationDate?: string | null;
  uuid: string;
  vin: string;
};

export function VehicleView() {
  const { t } = useTranslation(["common", "masterdata"]);
  const ability = useAbility();
  const canManage = ability.can(Action.Manage, "Vehicle");
  const deleteMutation = useVehicleDeleteMutation();

  const handleDeleteClick = useCallback(
    async (vehicle: Vehicle) => {
      await toastMutationStatus({
        mutationFn: () => deleteMutation.mutateAsync({ uuid: vehicle.uuid }),
        successMessage: t("common:VehicleDeleted", { vin: vehicle.vin }),
        errorMessage: t("common:ErrorDeletingVehicle", { vin: vehicle.vin }),
      });
    },
    [deleteMutation, t],
  );

  return (
    <Can I="read" a="Vehicle">
      <CenteredLayout
        variant={CenteredLayoutVariant.Table}
        title={t("common:Vehicles")}
        subTitle={t("common:SearchFilterAndManageEntriesDescription")}
      >
        <VehicleTable
          showExportButton
          defaultRowActions={{
            showDelete: canManage,
            showEdit: canManage,
            showView: true,
          }}
          showVehicleValuationAction={true}
          showCreateButton={canManage}
          createButtonLink="~/masterdata/vehicle/create"
          onDelete={handleDeleteClick}
          onRowNavigate={createRowNavigatePath("/masterdata/vehicle")}
          confirmActions={["delete"]}
          confirmLabels={{
            delete: {
              title: t("common:DeleteVehicle"),
              text: t("common:DeleteVehicleDescription"),
              confirmButton: t("common:Delete"),
              cancelButton: t("common:Cancel"),
            },
          }}
        />
      </CenteredLayout>
    </Can>
  );
}
